import { Component, Input, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SharedService } from '@shared/services/shared.service';
import { IResidentialHistory } from 'app/models/applicants/residential-history';
import { IStatus } from 'app/models/licenses/status';
import { IndividualApplicantService } from 'app/services/applicant/individual-applicant.service';

@Component({
  selector: 'residential-history',
  templateUrl: './residential-history.component.html',
  styleUrls: ['./residential-history.component.scss']
})
export class ResidentialHistoryComponent implements OnInit, OnDestroy {
  @Input() entityId: number = 0;
  @Input() statuses: IStatus[] = [];

  public history: IResidentialHistory = {
    id: 0,
    applicantId: this.entityId,
    city: '',
    state: '',
    country: '',
    from: '',
    to: '',
    archived: false
  }
  public fullHistory: IResidentialHistory[] = [];
  public historyForm = new FormGroup({
    city: new FormControl('', Validators.required),
    state: new FormControl('', Validators.required),
    country: new FormControl('', Validators.required),
    from: new FormControl<Date>(null, Validators.required),
    to: new FormControl<Date>(null)
  });

  public includeArchived: boolean = false;
  public addingHistory: boolean = false;

  public historyColumns: string [] = ['city', 'state', 'country', 'from', 'to', 'archived'];
  public historySource = new MatTableDataSource<IResidentialHistory>(this.fullHistory);
  @ViewChild('historyPaginator', {static: false}) historyPaginator: MatPaginator;
  @ViewChild('historySort', {static: false}) historySort: MatSort;

  public sharedService = inject(SharedService);
  public historyService = inject(IndividualApplicantService);

  ngOnInit(): void {
    this.getHistory(this.includeArchived);
  }

  ngOnDestroy(): void {
    if(this.addingHistory){
      if(this.history.id > 0){
        this.saveHistory();
      }
      else{
        this.createHistory();
      }
    }
  }

  getHistory(includeArchived: boolean): void {
    this.includeArchived = includeArchived;
    this.historyService.getResidentialHistory(this.entityId, this.includeArchived).subscribe({
      next: response => this.fullHistory = response,
      error: e => console.log('error getting history', e),
      complete: () => this.updateHistoryTable()
    });
  }

  updateHistoryTable(): void {
    setTimeout(() => {
      this.historySource.data = this.fullHistory;
      this.historySource.sort = this.historySort;
      this.historySource.paginator = this.historyPaginator;
    }, 500);
  }

  getEmptyHistory(): IResidentialHistory {
    return {
      id: 0,
      applicantId: this.entityId,
      city: '',
      state: '',
      country: '',
      from: '',
      to: '',
      archived: false
    }
  }

  updateHistoryForm(history: IResidentialHistory): void {
    this.historyForm.reset({
      city: history.city,
      state: history.state,
      country: history.country
    });

    if(history.from !== '' && history.from !== null){
      this.historyForm.get('from').setValue(this.sharedService.getDateFromString(history.from));
    }
    else{
      this.historyForm.get('from').setValue(null);
    }

    if(history.to !== '' && history.to !== null){
      this.historyForm.get('to').setValue(this.sharedService.getDateFromString(history.to));
    }
    else{
      this.historyForm.get('to').setValue(null);
    }

  }

  updateHistory(): void {
    let form = this.historyForm.value;
    this.history.city = form.city;
    this.history.state = form.state;
    this.history.country = form.country;
    this.history.from = form.from?.toDateString();
    this.history.to = form.to?.toDateString();
    this.history.applicantId = this.entityId;
  }

  toggleHistoryForm(adding: boolean): void {
    this.addingHistory = adding;
    this.updateHistoryForm(this.getEmptyHistory());
    if(!adding){
      this.updateHistoryTable();
      this.history = this.getEmptyHistory();
    }
  }

  selectHistory(history: IResidentialHistory):void {
    this.addingHistory = true;
    this.history = history;
    this.updateHistoryForm(history);
  }

  createHistory(): void {
    this.updateHistory();
    this.historyService.createResidentialHistory(this.history).subscribe({
      next: response => this.fullHistory.push(response),
      error: e => {
        this.sharedService.toastr.error("Could not create entry");
        console.log('create residential history error', e);
      },
      complete: () => {
        this.addingHistory = false;
        this.updateHistoryTable();
      }
    });
  }

  saveHistory(): void {
    this.updateHistory();
    this.historyService.updateResidentialHistory(this.history).subscribe({
      next: () => {
        let index: number = this.fullHistory.findIndex(a => a.id == this.history.id);
        this.fullHistory[index] = this.history;
      },
      error: e => {
        this.sharedService.toastr.error("Could not update history");
        console.log('error updating residential history', e);
      },
      complete: () => {
        this.history = this.getEmptyHistory();
        this.toggleHistoryForm(false);
      }
    });
  }

  archiveHistory(history: IResidentialHistory, archive: boolean): void {
    let archiving: string = archive? 'Archive ' : 'Un-Archive ';
    this.sharedService.openConfirm(archiving + 'this residential history entry?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.historyService.archiveResidentialHistory(history.id, archive).subscribe({
            complete: () => {
              history.archived = archive;
              if(archive){
                this.fullHistory = this.fullHistory.filter(a => a.id !== history.id);
              }
              this.toggleHistoryForm(false);
            },
            error: e => {
              this.sharedService.toastr.error('Could not archive entry');
              console.log('Archive residential history error', e);
            }
          });
        }
      }
    );
  }

  dateChange(event: Event, type: string): void {
    let input: string[] = ((event.target as HTMLInputElement).value).split('/');
    let year: number = parseInt(input[1]);
    let month: number = parseInt(input[0]) - 1;
    let date: Date = new Date(year, month, 1);

    if(type === 'from'){
      this.historyForm.patchValue({
        from: date
      });
    }
    else{
      this.historyForm.patchValue({
        to: date
      });
    }
  }

  setMonthAndYear(date: Date, datePicker: MatDatepicker<Date>, value: string){
    datePicker.close();
    if(value === 'from'){
      this.historyForm.patchValue({
        from: date
      });
    }
    else{
      this.historyForm.patchValue({
        to: date
      });
    }
  }

  public get locked(): boolean {
    return this.statuses.some(s => s.label === this.sharedService.submitted);
  }
}
